import React from 'react';
import { getFirestore, doc, updateDoc, runTransaction  } from 'firebase/firestore';

const OrdersView = ({ orders, isWorker, isAdmin, fetchOrders, deleteOrder }) => {
  const db = getFirestore();

  console.log("Orders in OrdersView:", orders);

  const fulfillOrder = async (order) => {
    try {
      await runTransaction(db, async (transaction) => {
        console.log('Order being fulfilled:', JSON.stringify(order, null, 2));

        // First, perform all reads
        const orderRef = doc(db, 'orders', order.id);
        const productUpdates = [];

        for (const item of order.items) {
          const productId = item.id || item.productId;
          if (!productId) {
            console.warn(`No product ID found for item: ${JSON.stringify(item)}`);
            continue;
          }

          const productRef = doc(db, 'Products', productId);
          const productDoc = await transaction.get(productRef);

          if (!productDoc.exists()) {
            console.warn(`Product ${productId} not found in the database`);
            continue;
          }

          const currentAmount = productDoc.data().amount;
          if (typeof currentAmount !== 'number') {
            console.warn(`Invalid amount for product ${productId}: ${currentAmount}`);
            continue;
          }

          const newAmount = Math.max(currentAmount - item.quantity, 0);
          productUpdates.push({ ref: productRef, newAmount });
        }

        // Then, perform all writes
        transaction.update(orderRef, { status: 'fulfilled' });

        for (const update of productUpdates) {
          transaction.update(update.ref, { amount: update.newAmount });
        }
      });

      console.log(`Order ${order.id} fulfilled and inventory updated`);
      fetchOrders(); // Refresh the orders list
    } catch (error) {
      console.error("Error fulfilling order:", error);
      alert(`Failed to fulfill order: ${error.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString();
    }
    return 'Date unavailable';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'fulfilled':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (orders.length === 0) {
    return <div>No orders to display.</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">{isWorker || isAdmin ? 'All Orders' : 'Your Orders'}</h2>
      {orders.map(order => (
        <div key={order.id} className="mb-6 p-4 border rounded-lg shadow-md">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-lg font-semibold">{order.userEmail || 'Email not available'}</p>
              <p className="text-sm text-gray-600">Order ID: {order.id}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-white font-semibold ${getStatusColor(order.status)}`}>
              {order.status || 'N/A'}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">Date: {formatDate(order.createdAt)}</p>
          <h3 className="font-semibold mt-2 mb-1">Items:</h3>
          <ul className="list-disc list-inside mb-2">
            {order.items && order.items.map((item, index) => (
              <li key={index} className="text-sm">
                {item.name} - Quantity: {item.quantity} - ${(item.price * item.quantity).toFixed(2)}
              </li>
            ))}
          </ul>
          <p className="font-bold text-lg mt-2">Total: ${order.total ? order.total.toFixed(2) : 'N/A'}</p>
          <div className="mt-3">
            {isWorker && order.status === 'pending' && (
              <button 
                onClick={() => fulfillOrder(order)}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mr-2"
              >
                Fulfill Order
              </button>
            )}
            {isAdmin && (
              <button 
                onClick={() => deleteOrder(order.id)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Delete Order
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default OrdersView;